import {
  HttpClient,
  HttpContext
} from '@angular/common/http';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
  throwError
} from 'rxjs';

import {
  TokenDecodeService
} from '../services/token-decode.service';

import {
  MenuStoreService
} from '../services/menu-store.service';

import {
  TokenStorageService
} from './token-storage.service';

import {
  ApiResponse
} from '../models/api-response.model';

import {
  API_ENDPOINTS
} from '../constants/api-endpoints.constants';

import {
  Authority
} from '../constants/authorities';

import {
  SKIP_AUTH
} from '../constants/auth-context';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;

  email?: string;
  phone?: string;
  roles?: string[];

  passwordResetRequired: boolean;
  resetPasswordFirstLogin: boolean;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface SetPasswordRequest {
  newPassword: string;
  confirmNewPassword: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private refreshRequest$:
    Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private tokenDecodeService: TokenDecodeService,
    private menuStore: MenuStoreService
  ) {}

  login(
    payload: LoginRequest
  ): Observable<LoginResponse> {

    return this.http
      .post<ApiResponse<LoginResponse>>(
        API_ENDPOINTS.AUTH.LOGIN,
        payload,
        {
          context:
            new HttpContext()
              .set(SKIP_AUTH, true)
        }
      )
      .pipe(
        map(response =>
          this.requireAuthResponse(
            response
          )
        ),

        tap(response =>
          this.startNewSession(
            response
          )
        )
      );
  }

  refreshAccessToken():
    Observable<string> {

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken =
      this.tokenStorage
        .getRefreshToken();

    if (
      !refreshToken ||
      this.tokenDecodeService
        .isJwtExpired(refreshToken)
    ) {
      this.logout(false);

      return throwError(() =>
        new Error(
          'Refresh token is missing or expired'
        )
      );
    }

    this.refreshRequest$ =
      this.http
        .post<ApiResponse<LoginResponse>>(
          API_ENDPOINTS.AUTH.REFRESH,
          {
            refreshToken
          },
          {
            context:
              new HttpContext()
                .set(SKIP_AUTH, true)
          }
        )
        .pipe(
          map(response =>
            this.requireAuthResponse(
              response
            )
          ),

          tap(response =>
            this.replaceTokens(
              response
            )
          ),

          map(response =>
            response.accessToken
          ),

          catchError(error => {
            this.logout(false);

            return throwError(
              () => error
            );
          }),

          finalize(() => {
            this.refreshRequest$ = null;
          }),

          shareReplay({
            bufferSize: 1,
            refCount: false
          })
        );

    return this.refreshRequest$;
  }

  ensureValidAccessToken():
    Observable<string> {

    const accessToken =
      this.tokenStorage
        .getAccessToken();

    if (
      accessToken &&
      !this.tokenDecodeService
        .isTokenExpired(5000)
    ) {
      return of(accessToken);
    }

    return this.refreshAccessToken();
  }

  changePassword(
    request: ChangePasswordRequest
  ): Observable<ApiResponse<void>> {

    return this.http.put<
      ApiResponse<void>
    >(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      request
    );
  }

  createFirstLoginPassword(
    request: SetPasswordRequest
  ): Observable<ApiResponse<void>> {

    return this.http.put<
      ApiResponse<void>
    >(
      API_ENDPOINTS.AUTH
        .FIRST_LOGIN_PASSWORD,
      request
    );
  }

  resetUserPassword(
    userId: string,
    request: SetPasswordRequest
  ): Observable<ApiResponse<void>> {

    return this.http.put<
      ApiResponse<void>
    >(
      API_ENDPOINTS.AUTH
        .RESET_PASSWORD(userId),
      request
    );
  }

  unlockUser(
    userId: string
  ): Observable<ApiResponse<void>> {

    return this.http.put<
      ApiResponse<void>
    >(
      API_ENDPOINTS.AUTH
        .UNLOCK(userId),
      {}
    );
  }

  logout(
    redirect = true
  ): void {
    this.refreshRequest$ = null;

    this.menuStore.clear();
    this.tokenDecodeService.clear();
    this.tokenStorage.clear();

    /*
     * Clear all data owned by this application tab,
     * including theme and language preferences.
     */
    sessionStorage.clear();

    if (redirect) {
      void this.router.navigate(
        ['/login'],
        {
          replaceUrl: true
        }
      );
    }
  }

  getAccessToken(): string | null {
    return this.tokenStorage
      .getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenStorage
      .getRefreshToken();
  }

  hasSession(): boolean {
    return this.tokenStorage
      .hasSession();
  }

  isAuthenticated(): boolean {
    return Boolean(
      this.getAccessToken() &&
      !this.tokenDecodeService
        .isTokenExpired()
    );
  }

  isFirstLoginPasswordResetRequired():
    boolean {

    return this.tokenDecodeService
      .getResetPasswordFirstLogin();
  }

  isPasswordResetRequired():
    boolean {

    return this.tokenDecodeService
      .getPasswordResetRequired();
  }

  isAnyPasswordActionRequired():
    boolean {

    return this.tokenDecodeService
      .isAnyPasswordActionRequired();
  }

  getCurrentUserRoles(): string[] {
    return this.tokenDecodeService
      .getRoles();
  }

  hasAuthority(
    authority: Authority
  ): boolean {
    return this.tokenDecodeService
      .hasAuthority(authority);
  }

  getCurrentUserId(): string | null {
    return this.tokenDecodeService
      .getUserId();
  }

  getCurrentUserEmail(): string | null {
    return this.tokenDecodeService
      .getEmail();
  }

  private startNewSession(
    response: LoginResponse
  ): void {
    /*
     * Remove every value associated with the previous
     * login in this browser tab.
     */
    this.menuStore.clear();
    this.tokenDecodeService.clear();
    this.tokenStorage.clear();
    sessionStorage.clear();

    this.replaceTokens(response);
  }

  private replaceTokens(
    response: LoginResponse
  ): void {
    this.menuStore.clear();

    this.tokenStorage.setAccessToken(
      response.accessToken
    );

    if (response.refreshToken) {
      this.tokenStorage.setRefreshToken(
        response.refreshToken
      );
    }

    this.tokenDecodeService.setToken(
      response.accessToken
    );
  }

  private requireAuthResponse(
    response: ApiResponse<LoginResponse>
  ): LoginResponse {
    const data = response?.data;

    if (!data?.accessToken) {
      throw new Error(
        'Access token is missing from response'
      );
    }

    return data;
  }
}